import { parse } from "date-fns";
import {
  ApiChallenge,
  ApiCreateChallengePayload,
  ApiUpdateChallengePayload,
  ApiVenueDetail,
} from "griptonite-api-client";
import { UseFormReturn } from "react-hook-form";
import { z } from "zod";

import urlSchema from "@/utils/urlSchema";

export const ChallengeDetailsFormSchema = z
  .object({
    venues: z.array(z.string().uuid()).min(1, { message: "At least one venue required" }),
    name: z.string({ message: "Name required" }).min(1, {
      message: "Name required",
    }),
    description: z.string().nullable(),
    startTime: z.string({ message: "Start time required" }).datetime(),
    endTime: z.string().nullable().optional(),
    isEndless: z.boolean().default(false),
    type: z.enum(["CHECKIN"]),
    media: z.array(
      z.object({
        uuid: z.string(),
      }),
    ),
    websiteUrl: urlSchema.nullable(),
    facebookUrl: urlSchema.nullable(),
    instagramUrl: urlSchema.nullable(),
  })
  .superRefine((obj, ctx) => {
    if (
      !obj.isEndless &&
      obj.endTime &&
      new Date(obj.startTime).getTime() >= new Date(obj.endTime).getTime()
    ) {
      ctx.addIssue({
        message: "End time must be after start time",
        code: z.ZodIssueCode.custom,
        path: ["endTime"],
      });
    }
    if (!obj.isEndless && !obj.endTime) {
      ctx.addIssue({
        message: "End time required when not endless",
        code: z.ZodIssueCode.custom,
        path: ["endTime"],
      });
    }
  });

export type ChallengeDetailsFormValues = z.infer<typeof ChallengeDetailsFormSchema>;

export const urlsArrayToFields = (urls: string[]) => {
  const fields = {
    websiteUrl: null,
    facebookUrl: null,
    instagramUrl: null,
  } as {
    websiteUrl: string | null;
    facebookUrl: string | null;
    instagramUrl: string | null;
  };

  urls.forEach((url) => {
    if (url.includes("facebook.com/")) {
      fields.facebookUrl = url;
    } else if (url.includes("instagram.com/")) {
      fields.instagramUrl = url;
    } else {
      fields.websiteUrl = url;
    }
  });

  return fields;
};

export const challengeToDetailsFormValues = (
  challenge?: ApiChallenge,
  venue?: ApiVenueDetail,
  startDate?: string,
  challengeType?: "CHECKIN",
): Omit<ChallengeDetailsFormValues, "startTime"> & {
  startTime: string | null;
} => {
  const startTimeFromStartDate = startDate
    ? parse(startDate, "yyyy-MM-dd", new Date()).toISOString()
    : undefined;
  return {
    venues: challenge?.venues.map((v) => v.uuid) || (venue?.uuid ? [venue.uuid] : []),
    name: challenge?.name || "",
    description: challenge?.description || null,
    startTime: challenge?.startTime || startTimeFromStartDate || null,
    endTime: challenge?.endTime || null,
    isEndless: challenge?.endTime === null,
    type: challenge?.type || challengeType || "CHECKIN",
    media: challenge?.media || [],
    ...urlsArrayToFields(challenge?.urls || []),
  };
};

export const challengeDetailsFormValuesToPayload = (
  values: ChallengeDetailsFormValues,
): ApiCreateChallengePayload => {
  return {
    venues: values.venues,
    name: values.name,
    description: values.description,
    startTime: values.startTime,
    endTime: values.isEndless ? null : values.endTime || null,
    type: values.type,
    coverImage: values.media.length > 0 ? values.media[0].uuid : null,
    media: values.media.map((m) => m.uuid),
    urls: [values.websiteUrl, values.facebookUrl, values.instagramUrl].filter(
      (url) => url,
    ) as string[],
  };
};

// Challenge Rules Form Schema
const ChallengeRewardSchema = z.object({
  name: z.string().min(1, { message: "Reward name required" }),
  description: z.string().nullable().optional(),
  image: z
    .object({
      uuid: z.string(),
    })
    .nullable()
    .optional(),
  uuid: z.string().optional(),
  quantityAvailable: z.number().min(1).nullable().optional(),
});

const ChallengeTierSchema = z.object({
  targetValue: z
    .number({ message: "Check-in goal required" })
    .min(1, { message: "Must be at least 1" })
    .nullable(),
  rewards: z.array(ChallengeRewardSchema).min(0),
  uuid: z.string().optional(),
});

export const ChallengeRulesFormSchema = z.object({
  minimumRoutesPerSession: z
    .number({ message: "Minimum routes required" })
    .min(1, { message: "Must be at least 1" }),
  allowSport: z.boolean().default(true),
  allowBouldering: z.boolean().default(true),
  allowReentry: z.boolean().default(false),
  tiers: z
    .array(ChallengeTierSchema)
    .min(1, { message: "At least one tier required" })
    .max(3, { message: "Maximum 3 tiers allowed" })
    .superRefine((obj, ctx) => {
      const targetValues = obj.map((tier) => tier.targetValue);
      // Target values must be monotonically increasing
      for (let i = 1; i < targetValues.length; i++) {
        if (
          targetValues[i] !== null &&
          targetValues[i - 1] !== null &&
          targetValues[i]! <= targetValues[i - 1]!
        ) {
          ctx.addIssue({
            message: "Check-in goals must be increasing",
            code: z.ZodIssueCode.custom,
            path: [i, "targetValue"],
          });
        }
      }
    }),
  lockMode: z.enum(["UNTIL_COMPLETED", "UNTIL_PREV_COMPLETED", "NONE"]),
});

export type ChallengeRulesFormValues = z.infer<typeof ChallengeRulesFormSchema>;

export const challengeToRulesFormValues = (challenge?: ApiChallenge): ChallengeRulesFormValues => {
  // Initialize with at least one tier
  const defaultTiers = [
    {
      targetValue: null,
      rewards: [],
    },
  ];

  const apiTiers = challenge?.tiers || [];
  const formTiers =
    apiTiers.length > 0
      ? apiTiers.map((tier) => ({
          targetValue: tier.targetValue,
          uuid: tier.uuid,
          rewards: tier.rewards.map((reward) => ({
            name: reward.name,
            description: reward.description,
            image: reward.image,
            uuid: reward.uuid,
            quantityAvailable: reward.quantityAvailable,
          })),
        }))
      : defaultTiers;

  return {
    minimumRoutesPerSession: challenge?.settings?.minimumRoutesPerSession || 3,
    allowSport: true, // Default values
    allowBouldering: true,
    allowReentry: challenge?.settings.allowReentry || false,
    tiers: formTiers,
    lockMode: challenge?.settings.lockMode || "NONE",
  };
};

export const challengeRulesFormValuesToPayload = (values: ChallengeRulesFormValues) => {
  return {
    settings: {
      minimumRoutesPerSession: values.minimumRoutesPerSession,
      allowReentry: values.allowReentry,
      lockMode: values.lockMode,
    },
    tiers: values.tiers.map((tier) => ({
      targetValue: tier.targetValue,
      uuid: tier.uuid,
      rewards: tier.rewards.map((reward) => ({
        name: reward.name,
        description: reward.description,
        image: reward.image?.uuid,
        uuid: reward.uuid,
        quantityAvailable: reward.quantityAvailable,
      })),
    })),
  };
};

export const ChallengePromotionFormSchema = z.object({
  notifications: z.object({
    challengeStart: z.object({
      enabled: z.boolean().default(true),
      text: z.string().max(120, { message: "Max 120 characters" }).nullable().optional(),
    }),
    challengeEndingSoon: z.object({
      enabled: z.boolean().default(false),
      text: z.string().max(120, { message: "Max 120 characters" }).nullable().optional(),
    }),
    checkinPromo: z.object({
      enabled: z.boolean().default(false),
      text: z.string().max(120, { message: "Max 120 characters" }).nullable().optional(),
    }),
    participationThanks: z.object({
      enabled: z.boolean().default(true),
      text: z.string().max(120, { message: "Max 120 characters" }).nullable().optional(),
    }),
  }),
  tvTakeover: z
    .object({
      enabled: z.boolean().default(false),
      media: z
        .object({ uuid: z.string(), url: z.string(), type: z.string() })
        .nullable()
        .optional(),
      frequency: z.enum(["LOW", "MEDIUM", "HIGH"]).default("MEDIUM"),
    })
    .superRefine((obj, ctx) => {
      if (obj.enabled && !obj.media) {
        ctx.addIssue({
          message: "Media required when TV takeover is enabled",
          code: z.ZodIssueCode.custom,
          path: ["media"],
        });
      }
    }),
  feedCard: z.object({
    enabled: z.boolean().default(false),
    headline: z.string().optional().nullable(),
    postText: z.string().optional().nullable(),
    image: z
      .object({
        uuid: z.string(),
        url: z.string(),
        type: z.string(),
      })
      .nullable()
      .optional(),
  }),
});

export type ChallengePromotionFormValues = z.infer<typeof ChallengePromotionFormSchema>;

export const challengeToPromotionFormValues = (
  challenge?: ApiChallenge,
): ChallengePromotionFormValues => {
  return {
    notifications: {
      challengeStart: {
        enabled: challenge?.settings.notifications?.challengeStart?.enabled ?? true,
        text:
          challenge?.settings.notifications?.challengeStart?.text ||
          "Earn rewards for climbing. Tap for more info",
      },
      challengeEndingSoon: {
        enabled: challenge?.settings.notifications?.challengeEndingSoon?.enabled ?? true,
        text:
          challenge?.settings.notifications?.challengeEndingSoon?.text ||
          "You're {{COUNT}} check-ins away from a reward. Tap to see your progress",
      },
      checkinPromo: {
        enabled: challenge?.settings.notifications?.checkinPromo?.enabled ?? true,
        text:
          challenge?.settings.notifications?.checkinPromo?.text ||
          "Join the challenge to get a reward for today's check-in!",
      },
      participationThanks: {
        enabled: challenge?.settings.notifications?.participationThanks?.enabled ?? false,
        text: challenge?.settings.notifications?.participationThanks?.text || "",
      },
    },
    tvTakeover: {
      enabled: challenge?.settings.tvTakeover?.enabled ?? false,
      media: challenge?.tvTakeoverMedia ?? null,
      frequency: challenge?.settings.tvTakeover?.frequency ?? "MEDIUM",
    },
    feedCard: {
      enabled: challenge?.settings.feedCard?.enabled ?? false,
      headline: challenge?.settings.feedCard?.headline || "",
      postText: challenge?.settings.feedCard?.postText || "",
      image: challenge?.feedCardImage ?? null,
    },
  };
};

export const challengePromotionFormValuesToPayload = (
  values: ChallengePromotionFormValues,
  touched: UseFormReturn<ChallengePromotionFormValues>["formState"]["touchedFields"],
): ApiUpdateChallengePayload => {
  const payload: ApiUpdateChallengePayload = { settings: {} };

  // Handle notifications
  const notifications: Record<string, { enabled: boolean; text?: string }> = {};
  for (const touchedKey of Object.keys(
    touched.notifications || {},
  ) as (keyof typeof values.notifications)[]) {
    notifications[touchedKey] = {
      enabled: values.notifications![touchedKey].enabled,
      text: touched.notifications?.[touchedKey]?.text
        ? values.notifications![touchedKey].text || undefined
        : undefined,
    };
  }

  if (Object.keys(notifications).length > 0) {
    payload.settings!.notifications = notifications as NonNullable<
      ApiUpdateChallengePayload["settings"]
    >["notifications"];
  }

  // Handle TV takeover if touched
  if (touched.tvTakeover) {
    payload.settings!.tvTakeover = {
      enabled: values.tvTakeover.enabled,
      frequency: values.tvTakeover.frequency,
    };
    payload.tvTakeoverMedia = values.tvTakeover.media ? values.tvTakeover.media.uuid : null;
  }

  if (touched.feedCard) {
    payload.settings!.feedCard = {
      enabled: values.feedCard.enabled,
      headline: values.feedCard.headline!,
      postText: values.feedCard.postText!,
    };
    payload.feedCardImage = values.feedCard.image ? values.feedCard.image.uuid : null;
  }

  return payload;
};
